/* 실행기. 모델이 낸 ToolCall 을 정본 dsl.json 의 factory 경로로 부른다.
 * 인자 규약: factory 는 위치 인자(정본 파라미터 순서). ref 는 개체 이름을 받는다.
 * 이름 계약: 이름은 그 이름을 만든 호출이 세운 유효 개체를 뜻한다. 호출 결과로 등록부를 만들고 뒤 호출의 참조는 등록부로 푼다.
 *   한 호출이 유효 개체를 여럿 세우면(교점 후보) 이름, 이름_2, 이름_3 … 이다. mathcore 가 교점 이름을 푸는 방식(getPointPositionByName)과 같다.
 * 사후조건: 호출이 유효 개체를 하나도 세우지 못하면 실패로 돌려주고 잔해를 지운다 */
import { acceptsOf, acceptsText, entryOf } from './dsl';
import { settleEngine, waitForCurves } from '../style';
import type { DslEntry, MathcoreApp, MathcoreObject, ToolCall, ToolResult } from './types';

type LiveObject = MathcoreObject & { auxiliaryPoints?: string[] };

function allObjects(app: MathcoreApp): LiveObject[] {
  return (app.objectManager?.getAllObjects?.() ?? []) as LiveObject[];
}

function byId(app: MathcoreApp, id: string): LiveObject | undefined {
  return (app.objectManager?.getObjectById?.(id) as LiveObject | undefined) ?? allObjects(app).find((o) => o.id === id);
}

/** 이름 → 유효 개체 id 들. 요청한 이름으로 만든 개체의 id 를 기억하고(이름이 아니라 id 로 찾는다), 없으면 화면에 이미 있는 유효 개체(교점 그릇이면 그 유효 후보) */
export class Registry {
  private names = new Map<string, string[]>();
  /** 모델이 보는 별칭(o1, o2 …). mathcore 의 개체 번호와 자동 이름은 화면에서 이어 세어져 실행마다 달라지므로 모델에게 보이지 않는다. 별칭은 실행마다 0부터 센다 */
  private aliasById = new Map<string, string>();
  private idByAlias = new Map<string, string>();

  constructor(private app: MathcoreApp) {}

  set(name: string, ids: string[]): void { this.names.set(name, ids); }

  /** 개체의 별칭. 처음 물을 때 순서대로 매긴다(묻는 순서는 개체를 만든 순서라 같은 입력이면 같다) */
  aliasOf(id: string): string {
    let alias = this.aliasById.get(id);
    if (!alias) { alias = `o${this.aliasById.size + 1}`; this.aliasById.set(id, alias); this.idByAlias.set(alias, id); }
    return alias;
  }

  /** 이름으로 찾는다. 조각으로 나뉜 곡선(이름_1, 이름_2)은 이름만으로 첫 조각을 가리킨다 */
  get(name: string): string[] {
    const ids = this.lookup(name);
    return ids.length ? ids : this.lookup(`${name}_1`);
  }

  /** 등록부가 기억하는 이름 중 개체가 아직 화면에 있는 것 */
  knownNames(): string[] {
    return [...this.names.keys()].filter((n) => this.names.get(n)?.some((id) => byId(this.app, id)));
  }

  /** 이름으로 기억한 첫 개체 */
  objectOf(name: string): LiveObject | undefined {
    for (const id of this.names.get(name) ?? []) { const o = byId(this.app, id); if (o) return o; }
    return undefined;
  }

  /** 요청한 이름으로 기억한 개체 id 의 역조회. 목록에 보일 이름표 */
  labelOf(id: string): string | undefined {
    for (const [name, ids] of this.names) if (ids.includes(id)) return name;
    return undefined;
  }

  /** 개체가 쓸 수 있는 id: 유효하면 자기 자신, 교점 그릇이면 그 유효 후보 */
  private validIds(o: LiveObject): string[] {
    if (o.isValid !== false) return [o.id];
    const out: string[] = [];
    for (const aux of o.auxiliaryPoints ?? []) { const p = byId(this.app, aux); if (p && p.isValid !== false) out.push(p.id); }
    return out;
  }

  /** 가리키는 글을 개체로 푼다. 개체는 만들 때 팩토리가 돌려준 id 로 가리킨다(별칭, id). 요청한 이름은 같은 묶음 안에서 아직 id 를 모를 때의 이름표일 뿐이고, mathcore 가 자동으로 붙인 화면의 이름으로는 찾지 않는다(이름은 겹치고 바뀐다) */
  private lookup(name: string): string[] {
    const aliased = this.idByAlias.get(name);
    const target = aliased ? byId(this.app, aliased) : undefined;
    if (target) return this.validIds(target);
    const direct = byId(this.app, name);
    if (direct) return this.validIds(direct);
    const own = (this.names.get(name) ?? []).filter((id) => byId(this.app, id));
    return own;
  }
}

const AXIS_IDS = ['0:xAxis', '0:yAxis'];

function parseArgs(call: ToolCall): Record<string, unknown> | null {
  try {
    const v = JSON.parse(call.function.arguments || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function positional(entry: DslEntry, args: Record<string, unknown>, registry: Registry): { values: unknown[]; missing: string[]; unknownRefs: string[] } {
  const missing: string[] = [];
  const unknownRefs: string[] = [];
  const accepts = acceptsOf(entry);
  const ref = (v: unknown, slot: string): unknown => {
    if (typeof v !== 'string') return v;
    // 좌표축은 개체 id 가 아니라 문자열 id 로 가리키고, 받는 자리(accepts 에 axis)에서만 그대로 넘긴다
    if (AXIS_IDS.includes(v) && accepts.get(slot)?.includes('axis')) return v;
    const ids = registry.get(v);
    if (!ids.length) { unknownRefs.push(v); return v; }
    return ids[0];
  };
  const values = entry.params.map((p) => {
    const v = args[p.name];
    if (v === undefined || v === null || v === '') { if (!p.optional) missing.push(p.name); return undefined; }
    if (p.type === 'ref') return ref(v, p.name);
    if (p.type.startsWith('ref[')) return Array.isArray(v) ? v.map((one) => ref(one, p.name)) : v;
    return v;
  });
  return { values, missing, unknownRefs };
}

function factoryMethod(app: MathcoreApp, path: string[]): ((...a: unknown[]) => unknown) | null {
  let node: unknown = app.objectManager?.factory;
  for (const key of path) {
    if (!node || typeof node !== 'object') return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === 'function' ? (node as (...a: unknown[]) => unknown) : null;
}

/** 공개 래퍼 deleteObjectById 로 지운다. 그릇 개체의 보조점도 같이 */
function removeObjects(app: MathcoreApp, ids: string[]): void {
  const manager = app.objectManager as (Record<string, unknown> & { deleteObjectById?: (...ids: string[]) => void }) | undefined;
  if (!manager?.deleteObjectById) return;
  const all = new Set<string>();
  for (const id of ids) { all.add(id); for (const aux of byId(app, id)?.auxiliaryPoints ?? []) all.add(aux); }
  for (const id of all) { try { manager.deleteObjectById(id); } catch { /* 부모와 함께 지워진 개체 */ } }
}

/** 결과가 비는 것이 정상인 도구 */
const EMPTY_IS_RESULT = ['point.intersect'];

/** 실패 문장 뒤에 붙일, 이 도구가 받는 종류 */
const hint = (entry: DslEntry): string => { const text = acceptsText(entry); return text ? `. ${text}` : ''; };

/** 호출 하나를 실행하고 세운 개체를 등록부에 올린다 */
export async function executeOne(app: MathcoreApp, call: ToolCall, registry: Registry = new Registry(app)): Promise<ToolResult> {
  const name = call.function.name;
  const entry = entryOf(name);
  if (!entry) return { call, ok: false, method: name, detail: `${name} 은 dsl.json 에 없는 도구다` };
  if (entry.backend !== 'factory' || !entry.path) return { call, ok: false, method: name, detail: `${name} 은 factory 도구가 아니다. 이 실행기는 math2d factory 만 부른다` };
  const args = parseArgs(call);
  if (!args) return { call, ok: false, method: name, detail: `${name} 의 인자가 JSON 객체가 아니다` };
  const { values, missing, unknownRefs } = positional(entry, args, registry);
  if (missing.length) return { call, ok: false, method: name, detail: `${name} 의 필수 인자가 비었다: ${missing.join(', ')}` };
  if (unknownRefs.length) return { call, ok: false, method: name, detail: `${name} 이 가리킨 ${unknownRefs.map((r) => `"${r}"`).join(', ')} 은 세워진 개체의 이름이 아니다` };
  const fn = factoryMethod(app, entry.path);
  const method = `factory.${entry.path.join('.')}`;
  if (!fn) return { call, ok: false, method, detail: `${method} 가 화면 인스턴스에 없다` };
  const before = new Set(allObjects(app).map((o) => o.id));
  let response: unknown;
  try {
    response = await fn(...values);
  } catch (error) {
    return { call, ok: false, method, detail: `${method} 예외: ${error instanceof Error ? error.message : String(error)}${hint(entry)}` };
  }
  const made = allObjects(app).filter((o) => !before.has(o.id)).map((o) => byId(app, o.id) ?? o);
  // 주 개체는 팩토리가 돌려준 id 로만 정한다(생성 때 발급된 id 가 유일한 손잡이다). 돌려주지 않았는데 새로 생긴 아무 개체를 주 개체로 삼으면 보조 점 같은 다른 개체를 성공으로 보고한다
  const primaryId = response && typeof response === 'object' && typeof (response as { id?: unknown }).id === 'string' ? (response as { id: string }).id : '';
  if (!primaryId) {
    if (made.length) removeObjects(app, made.map((o) => o.id));
    return { call, ok: false, method, detail: `${method} 가 개체를 돌려주지 않았다(만든 개체의 id 가 없어 가리킬 수 없다). 잔해는 지웠다${hint(entry)}` };
  }
  const primary = byId(app, primaryId) ?? made[0];
  if (!primary) return { call, ok: false, method, detail: `${method} 가 돌려준 id ${primaryId} 의 개체가 화면에 없다${hint(entry)}` };
  // 이름을 안 준 개체는 별칭으로 부른다. mathcore 의 자동 이름은 화면 안에서 이어 세어져 모델 입력에 싣지 않는다
  const requested = typeof args.name === 'string' && args.name ? args.name : '';
  const sceneName = requested || registry.aliasOf(primaryId);
  // 선 개체: 주 개체가 유효하면 그것, 아니면 그 호출이 만든 같은 종류의 유효 개체들(교점 후보). 이름표(text)는 세지 않는다
  const standing = primary.isValid !== false
    ? [primary]
    : made.filter((o) => o.id !== primaryId && o.type !== 'text' && o.isValid !== false && (primary.type === undefined || o.type === primary.type));
  if (!standing.length) {
    removeObjects(app, [primaryId, ...made.map((o) => o.id)]);
    // 만나지 않는 두 곡선의 교점만 호출이 올바르고 결과가 빈 것이다. 실패가 아니라 0개다. 그 밖의 도구가 유효한 개체를 못 세우면 실패다(조사: docs/palette)
    if (EMPTY_IS_RESULT.includes(name)) { if (requested) registry.set(sceneName, []); return { call, ok: true, method, detail: JSON.stringify({ id: registry.aliasOf(primaryId), name: sceneName, standing: 0 }), response: { id: primaryId, name: sceneName, standing: [], made: [] } }; }
    return { call, ok: false, method, detail: `"${sceneName}" 이 유효하게 서지 않았다(인자의 종류나 값이 맞지 않거나 식을 엔진이 읽지 못했다). 잔해는 지웠다${hint(entry)}`, response: { id: primaryId, name: sceneName } };
  }
  standing.forEach((o, i) => { if (requested) registry.set(i === 0 ? sceneName : `${sceneName}_${i + 1}`, [o.id]); else registry.aliasOf(o.id); });
  const madeNow = made.map((o) => ({ id: o.id, name: o.name, type: o.type, isValid: o.isValid, position: o.position }));
  return { call, ok: true, method, detail: JSON.stringify({ id: registry.aliasOf(primaryId), name: sceneName, standing: standing.length }), response: { id: primaryId, name: sceneName, standing: standing.map((o) => ({ id: o.id, type: o.type, position: o.position })), made: madeNow } };
}

const registries = new WeakMap<MathcoreApp, Registry>();

/** 앱마다 하나의 등록부. 단계가 달라도 앞 단계가 만든 개체를 요청한 이름으로 가리킬 수 있다 */
export function registryOf(app: MathcoreApp): Registry {
  let registry = registries.get(app);
  if (!registry) { registry = new Registry(app); registries.set(app, registry); }
  return registry;
}

/** 화면을 비울 때 등록부도 비운다 */
export function resetRegistry(app: MathcoreApp): void {
  registries.delete(app);
}

/** 호출 묶음을 순서대로 실행한다. 앞 호출이 세운 이름을 뒤 호출이 가리킨다.
 * 호출마다 엔진이 평가를 마칠 때까지 기다린다. 곡선 복사본(변환)을 만든 직후 같은 묶음에서 접선을 세우면 평가가 끝나기 전이라 접선이 무효로 서고(3회 중 3회 재현), 호출 사이에 안정을 기다리면 선다(실측 2026-10-19) */
export async function executeToolCalls(app: MathcoreApp, calls: ToolCall[], afterEach?: (names: string[]) => Promise<void>): Promise<ToolResult[]> {
  const registry = registryOf(app);
  const out: ToolResult[] = [];
  for (const call of calls) {
    await waitForCurves(app);
    out.push(await executeOne(app, call, registry));
    await settleEngine(app);
    // 선언 이름 경계 캡처(기획 4항): 호출 하나가 끝날 때마다 지금 선 이름을 알려 풀이 단계가 다 섰는지 보게 한다
    if (afterEach) await afterEach(registry.knownNames());
  }
  return out;
}
